import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import axios from 'axios';
import { Phone, PhoneOff, Loader2, Send, Mic, MicOff, X, ImagePlus } from 'lucide-react';
import {
  CHAT_IMAGE_ACCEPT,
  CHAT_IMAGE_TOPIC,
  useChatImageAttachments,
  imageFilesFrom,
  ChatAttachmentStrip,
  ChatMessageImages,
  chatImagesFromIds,
  imageTranscriptText,
  stripImageMarker,
  modelSupportsImages,
  useImageDrop,
  ChatDropOverlay,
} from './chatImageAttachments';

// Public embed API instance (no credentials — mirrors EmbedLiveAvatarPage)
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false,
  headers: { 'Content-Type': 'application/json' },
});

// LiveKit default chat topic. The voice agent's RoomIO registers a text-stream
// handler on this topic and treats an incoming message as a user turn
// (interrupt + generate_reply), so a typed reply is understood exactly like speech.
const CHAT_TOPIC = 'lk.chat';

// LiveKit's transcription topic. RoomIO streams both sides of the conversation here as
// it happens: agent text while the LLM produces it, user text from STT (interim, then
// final). The backend's own data-channel transcript only fires once a turn is committed
// to history, which with an avatar lands a full turn late, so this is the live source.
const TRANSCRIPTION_TOPIC = 'lk.transcription';
const SEGMENT_ID_ATTR = 'lk.segment_id';

const AVATAR_IDENTITY = 'liveavatar-avatar-agent';

// The identity the backend watches for to activate the avatar. Also tells us which side
// of a transcription stream is the visitor.
const LOCAL_IDENTITY = 'liveavatar-user';

const normalizeText = (text) => (text || '').replace(/\s+/g, ' ').trim();

// Compare on letters and digits only. An interim transcript arrives without the casing
// and punctuation the final one gets ("hey my card" then "Hey. My card got stolen."), so
// raw text will not line up while a turn is still being spoken.
const compareKey = (text) => {
  const stripped = (text || '').toLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
  return stripped || normalizeText(text).toLowerCase();
};

// A caption belongs to the same turn as `text` when the two are equal or one is still
// catching up with the other: a live segment grows word by word, and an interrupted agent
// turn is committed as only the part that was actually spoken.
const isSameTurn = (caption, speaker, text) => {
  if (caption.speaker !== speaker) return false;
  const a = compareKey(caption.text);
  const b = compareKey(text);
  return a === b || a.startsWith(b) || b.startsWith(a);
};

// Streamed captions are keyed by segment; data-channel captions are not.
const isStreamedKey = (key) => typeof key === 'string' && key.startsWith('segment-');

const longerOf = (a, b) => (b.length >= a.length ? b : a);
// A turn the visitor cut off can only ever shrink: every copy of it that arrives
// later was written by the LLM, not spoken by the avatar.
const shorterOf = (a, b) => (b.length <= a.length ? b : a);

// Agent captions are paced against the avatar's audio. LiveKit's own transcript
// synchronizer is disabled in an avatar session, because the avatar replaces the audio
// output after the session starts, so agent text arrives as fast as the LLM writes it.
// The panel holds it back and reveals it in step with the speech it is playing.
const REVEAL_TICK_MS = 80;
// A background tab throttles setInterval to about once a second. Clamping the
// gap tighter than that loses speech the avatar really did play, so the text
// falls further behind every tick the tab is hidden.
const MAX_TICK_MS = 1200;
const DEFAULT_CHARS_PER_SEC = 15; // ~150 words per minute
const MIN_CHARS_PER_SEC = 8;
const MAX_CHARS_PER_SEC = 32;
const VOICE_RMS_THRESHOLD = 0.01;
// A voice the browser mixes down never clears a fixed threshold, and the reveal
// would then wait out the whole turn on a track that is plainly talking. When
// the loudest thing recently heard sits under the threshold but above the noise
// floor, the gate drops to a fraction of it instead.
const QUIET_TRACK_FLOOR = 0.0015;
const QUIET_GATE_RATIO = 0.4;
const PEAK_DECAY_PER_TICK = 0.972; // ~2s half-life: the gate follows this turn
const VOICE_HANGOVER_MS = 400; // bridge the pauses between words
const END_OF_SPEECH_SILENCE_MS = 900; // silence after the text ended closes the turn
const NO_AUDIO_FALLBACK_MS = 4000; // no measurable audio: reveal anyway rather than stall
// A text stream that never closed (the turn errored out) must not sit at the
// head of the queue holding every later turn behind it.
const ORPHAN_SEGMENT_MS = 2500;
// A visitor turn starting while the avatar is mid-sentence is a barge-in: the
// agent stops there, so the rest of the reply is never spoken and must never be
// written. The window around the moment the avatar fell silent is deliberately
// lopsided. Talking over the avatar reaches this document at or shortly before
// that moment, because the interrupt fires off the same speech; answering a
// question the moment it finished reaches it afterwards, and is not a barge-in.
const BARGE_IN_LEAD_MS = 2000;
const BARGE_IN_LAG_MS = 600;
const CUT_MARKER = '\u2026';
const CUT_MEMORY = 4; // recent cut turns remembered, so a late commit cannot undo one
// How long a streamed bubble can still be taken over by another copy of the same
// utterance. LiveKit republishes one utterance under a fresh segment id whenever
// its transcription output is not mid-capture, so the same words arrive on a key
// this panel has never seen and land as a second bubble. Prefix matching alone
// would fix that and cause a worse bug: across a whole conversation it would fold
// a later "yes" into an earlier "yes please". A few seconds is longer than any
// interim-to-final gap and far shorter than a turn worth confusing it with.
const SAME_UTTERANCE_MS = 4000;

// 100dvh keeps the composer on screen when mobile browser chrome overlaps the viewport.
// The h-screen class stays on as the fallback for browsers that do not support dvh.
const FULL_HEIGHT = { height: '100dvh' };

// Reveal only whole words, so text does not appear mid-word
const revealedText = (full, count) => {
  if (count >= full.length) return full;
  const slice = full.slice(0, Math.max(0, Math.floor(count)));
  const boundary = slice.lastIndexOf(' ');
  return boundary > 0 ? slice.slice(0, boundary) : '';
};

/**
 * Clear everything the reveal loop carries between turns. The learned speaking
 * rate is deliberately kept: it belongs to this agent's voice, not to a session.
 */
const resetRevealState = (state) => {
  state.segments = [];
  state.lastVoiceAt = 0;
  state.lastTickAt = 0;
  state.peakLevel = 0;
  state.carryVoice = false;
  state.bargeInAt = 0;
  state.cutTurns = [];
};

/**
 * Learn this voice's real speaking rate from a turn that was heard end to end.
 * A turn that was cut off, or that ran blind because nothing measurable came off
 * the track, says nothing about how fast the avatar speaks: a barge-in three
 * words into a long answer looks like an impossibly fast talker, and feeding
 * that in is what leaves the next few turns visibly out of step.
 */
const learnSpeakingRate = (state, segment) => {
  if (segment.blindMs > 0) return;
  if (segment.spokenMs <= 1000 || segment.full.length <= 20) return;
  const measured = (segment.full.length / segment.spokenMs) * 1000;
  state.charsPerSec = Math.min(
    MAX_CHARS_PER_SEC,
    Math.max(MIN_CHARS_PER_SEC, state.charsPerSec * 0.7 + measured * 0.3)
  );
};

const withCutMarker = (text) => (text.endsWith(CUT_MARKER) ? text : text + CUT_MARKER);

// Still being written, so another copy of the same words is the same utterance
// rather than a new turn that happens to start the same way. `updatedAt` moves with
// every chunk, which matters for a long utterance whose first interim landed ten
// seconds before its final.
const stillOpen = (caption, now) => now - (caption.updatedAt || caption.at || 0) < SAME_UTTERANCE_MS;

const isAvatarParticipant = (participant) =>
  participant.identity === AVATAR_IDENTITY ||
  participant.name === AVATAR_IDENTITY ||
  (participant.identity && participant.identity.includes('liveavatar')) ||
  (participant.name && participant.name.includes('liveavatar'));

const EmbedAvatarPanelPageContent = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarVideoTrack, setAvatarVideoTrack] = useState(null);
  const [micEnabled, setMicEnabled] = useState(true);
  const [textValue, setTextValue] = useState('');
  const [sending, setSending] = useState(false);
  // Visitor-attached images (agent setting chat_image_upload_enabled).
  const attachments = useChatImageAttachments(agentId, undefined, agent?.chat_image_limits?.per_message);
  const imageInputRef = useRef(null);
  const imagesEnabled = Boolean(agent?.chat_image_upload_enabled) && modelSupportsImages(agent?.llm_model);
  const imageDrop = useImageDrop(imagesEnabled && isConnected, attachments.addFiles);
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]);
  const [dynamicVariableValues, setDynamicVariableValues] = useState({});

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const videoAttachedRef = useRef(false);
  const avatarReadyNotifiedRef = useRef(false);
  const transcriptEndRef = useRef(null);
  const captionSeqRef = useRef(0);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const analyserDataRef = useRef(null);
  const revealTimerRef = useRef(null);
  const revealRef = useRef({
    segments: [], // agent segments waiting to be spoken: { key, full, shown, closed, ... }
    charsPerSec: DEFAULT_CHARS_PER_SEC,
    lastVoiceAt: 0,
    lastTickAt: 0,
    peakLevel: 0,      // decaying loudest RMS, for the quiet-track voice gate
    carryVoice: false, // hand the voice clock to the next segment, mid-breath
    bargeInAt: 0,      // when the visitor last started a turn of their own
    cutTurns: [],      // full text of turns the visitor cut off
  });

  // Extract dynamic variables from the query string (Name, Email, ...)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const systemParams = ['view', 'token', 'embed_token', 'key', 'embed', 'agent'];
    const names = [];
    const initial = {};
    params.forEach((value, key) => {
      if (!systemParams.includes(key)) {
        names.push(key);
        if (value && !value.startsWith('{{') && !value.endsWith('}}')) {
          initial[key] = value;
        }
      }
    });
    setDynamicVariableNames(names);
    setDynamicVariableValues(initial);
  }, []);

  // Load agent config
  useEffect(() => {
    const loadAgent = async () => {
      try {
        const response = await api.get(`/agents/${agentId}`);
        if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          setError('Received unexpected response format. Please check your API configuration.');
          setLoading(false);
          return;
        }
        setAgent(response.data);

        const raw = response.data.liveavatar_enabled;
        const enabled = raw === true || raw === 'true' || raw === 1 || raw === '1';
        const avatarId = response.data.liveavatar_avatar_id;
        const hasAvatarId = avatarId && typeof avatarId === 'string' && avatarId.trim() !== '';
        if (!enabled) {
          setError('LiveAvatar is not enabled for this agent.');
        } else if (!hasAvatarId) {
          setError('Avatar ID is missing for this agent.');
        }
      } catch (err) {
        setError(`Agent not found: ${err.response?.data?.detail || err.message}`);
      } finally {
        setLoading(false);
      }
    };
    if (agentId) loadAgent();
    else setLoading(false);
  }, [agentId]);

  // Live caption from a transcription stream. Keyed by segment so a growing segment
  // updates its own bubble instead of stacking a new one per chunk.
  const upsertCaption = useCallback((key, speaker, text, opts = {}) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const cut = !!opts.cut;
    const now = Date.now();
    setCaptions((prev) => {
      const index = prev.findIndex((c) => c.key === key);
      if (index !== -1) {
        // A bubble holding a cut-off turn holds exactly the words the visitor
        // heard. Nothing may grow it back into the reply the LLM wrote.
        if (prev[index].cut && !cut) return prev;
        if (prev[index].text === clean && !!prev[index].cut === cut) return prev;
        const next = [...prev];
        next[index] = { ...next[index], text: clean, cut: cut || !!next[index].cut, updatedAt: now };
        return next;
      }
      // This turn may already have a bubble under a different key: the backend
      // publishes every turn on the data channel, and LiveKit republishes one
      // utterance under a fresh segment id whenever its transcription output is not
      // mid-capture - a second final event for the same words then arrives on a key
      // never seen before. Take the bubble over instead of adding a second one for
      // the same words.
      const adoptIndex = prev.findIndex(
        (c, i) =>
          i >= prev.length - 3 &&
          isSameTurn(c, speaker, clean) &&
          // A bubble from the data channel is this turn wherever it sits. One that
          // streamed has to still be open, and to be growing rather than shrinking,
          // or two separate turns could be folded together.
          (!isStreamedKey(c.key) || (stillOpen(c, now) && clean.length >= c.text.length))
      );
      if (adoptIndex !== -1) {
        const next = [...prev];
        const held = next[adoptIndex];
        next[adoptIndex] = {
          ...held,
          key,
          text: (cut || held.cut) ? shorterOf(held.text, clean) : longerOf(held.text, clean),
          cut: cut || !!held.cut,
          updatedAt: now,
        };
        return next;
      }
      return [...prev, {
        key, speaker, text: clean, cut, at: now, updatedAt: now,
        timestamp: new Date().toLocaleTimeString(),
      }];
    });
  }, []);

  // Add a caption unless this turn is already on screen. The backend republishes every
  // turn on the data channel once it is committed to history, which lands after the live
  // stream and after a typed message is echoed, so those copies must not double up.
  const addCaption = useCallback((speaker, text, images) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const state = revealRef.current;
    const now = Date.now();
    if (speaker !== 'user') {
      // An agent turn still being released stays under the reveal loop's control, so the
      // committed copy must not race ahead and dump the rest of the text on screen.
      if (state.segments.some((s) => isSameTurn({ speaker, text: s.full }, speaker, clean))) {
        return;
      }
      // A turn already retired as cut keeps the words that were spoken. The backend
      // republishes every turn once it is committed to history, and for a cut turn that
      // copy still carries the whole reply.
      if (state.cutTurns.some((full) => isSameTurn({ speaker: 'ai', text: full }, 'ai', clean))) {
        return;
      }
    }
    captionSeqRef.current += 1;
    const key = `turn-${captionSeqRef.current}`;
    setCaptions((prev) => {
      // Same words already shown, by the live stream or by a typed echo
      const shown = compareKey(clean);
      if (prev.slice(-8).some((c) => c.speaker === speaker && compareKey(c.text) === shown)) {
        return prev;
      }
      // A live bubble for this turn that has not caught up yet. The visitor's spoken
      // turn used to be looked for in the newest bubble alone, on the grounds that it is
      // the newest while it streams. It is not: the agent's own paced bubble is appended
      // while the visitor is still talking, and the committed copy then landed as a
      // second bubble for words already on screen.
      const mergeIndex = prev.findIndex(
        (c, i) =>
          i >= prev.length - 3 &&
          isSameTurn(c, speaker, clean) &&
          (speaker !== 'user' || (isStreamedKey(c.key) && stillOpen(c, now)))
      );
      if (mergeIndex !== -1) {
        const next = [...prev];
        const held = next[mergeIndex];
        next[mergeIndex] = {
          ...held,
          // A cut turn can only shrink: this copy came from the LLM, and the words past
          // the cut were never spoken.
          text: held.cut ? shorterOf(held.text, clean) : longerOf(held.text, clean),
          updatedAt: now,
          ...(images?.length && !held.images ? { images } : {}),
        };
        return next;
      }
      return [...prev, {
        key, speaker, text: clean, at: now, updatedAt: now,
        timestamp: new Date().toLocaleTimeString(),
        ...(images?.length ? { images } : {}),
      }];
    });
  }, []);

  // Voice activity on the avatar's audio track: the only reliable read on how far the
  // avatar has actually got through a turn.
  const attachAudioAnalyser = useCallback((track) => {
    try {
      const mediaStreamTrack = track?.mediaStreamTrack;
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!mediaStreamTrack || !AudioCtx) return;
      if (!audioCtxRef.current) audioCtxRef.current = new AudioCtx();
      const ctx = audioCtxRef.current;
      ctx.resume?.().catch(() => {});
      // Analyse only, never connect to the destination: the audio element plays this track
      const source = ctx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      source.connect(analyser);
      analyserRef.current = analyser;
      analyserDataRef.current = new Uint8Array(analyser.fftSize);
    } catch {
      // no analyser available: the reveal falls back to a steady pace
    }
  }, []);

  /**
   * The visitor has started a turn of their own, which is what stops the avatar
   * mid-sentence.
   *
   * Only the first stamp taken against the turn now playing counts. LiveKit publishes
   * the visitor's interim and final transcripts as separate streams, and letting the
   * final one move the stamp would push a real barge-in past the moment the avatar fell
   * silent, where it reads as a reply that came after.
   */
  const markBargeIn = useCallback(() => {
    const state = revealRef.current;
    const head = state.segments[0];
    if (!head || !head.activatedAt || state.bargeInAt < head.activatedAt) {
      state.bargeInAt = Date.now();
    }
  }, []);

  // RMS of the avatar's audio, or null when there is nothing to measure. The
  // reveal loop turns it into speaking / not speaking against an adaptive gate,
  // so a quietly mixed voice does not read as silence for the whole turn.
  const readAvatarLevel = useCallback(() => {
    const analyser = analyserRef.current;
    const data = analyserDataRef.current;
    if (!analyser || !data) return null;
    analyser.getByteTimeDomainData(data);
    let sum = 0;
    for (let i = 0; i < data.length; i += 1) {
      const sample = (data[i] - 128) / 128;
      sum += sample * sample;
    }
    return Math.sqrt(sum / data.length);
  }, []);

  // Hold agent text back and let the reveal loop release it as the avatar speaks it
  const pushAgentText = useCallback((key, text, closed) => {
    const clean = normalizeText(text);
    if (!clean) return;
    const state = revealRef.current;
    let segment = state.segments.find((s) => s.key === key);
    if (!segment) {
      segment = {
        key,
        full: clean,
        shown: 0,
        closed: false,
        spokenMs: 0,
        blindMs: 0,         // time revealed with no measurable audio to pace against
        activatedAt: 0,
        grewAt: Date.now(), // last time more text arrived, for the orphan watchdog
      };
      state.segments.push(segment);
    }
    if (clean.length >= segment.full.length) {
      if (clean.length > segment.full.length) segment.grewAt = Date.now();
      segment.full = clean;
    }
    if (closed) segment.closed = true;
  }, []);

  // Release the current agent segment in step with the audio, one tick at a time
  const tickReveal = useCallback(() => {
    const state = revealRef.current;
    const segment = state.segments[0];
    const now = Date.now();
    const dtMs = state.lastTickAt ? Math.min(now - state.lastTickAt, MAX_TICK_MS) : REVEAL_TICK_MS;
    state.lastTickAt = now;

    // Voice gate, relative to the loudest thing recently heard on this track so a
    // quiet mix still reads as speech - see QUIET_TRACK_FLOOR.
    const level = readAvatarLevel();
    if (level !== null) state.peakLevel = Math.max(level, state.peakLevel * PEAK_DECAY_PER_TICK);
    const gate =
      state.peakLevel >= VOICE_RMS_THRESHOLD || state.peakLevel < QUIET_TRACK_FLOOR
        ? VOICE_RMS_THRESHOLD
        : state.peakLevel * QUIET_GATE_RATIO;
    const speaking = level === null ? null : level > gate;
    if (speaking) state.lastVoiceAt = now;
    if (!segment) return;

    if (!segment.activatedAt) {
      // this segment has not been heard yet, whatever the previous one did
      segment.activatedAt = now;
      // Segments that run back to back in one breath keep the voice clock, so the
      // next one carries on against audio that is already playing instead of
      // waiting out a silence that is never coming.
      if (state.carryVoice) state.carryVoice = false;
      else state.lastVoiceAt = speaking ? now : 0;
    }

    const heardVoice = state.lastVoiceAt > 0;
    // Nothing measurable on the track: pace the text rather than stall the transcript
    const noAudioSignal =
      speaking === null || (!heardVoice && now - segment.activatedAt > NO_AUDIO_FALLBACK_MS);
    const withinSpeech = speaking || (heardVoice && now - state.lastVoiceAt < VOICE_HANGOVER_MS);

    if (withinSpeech || noAudioSignal) {
      segment.shown = Math.min(
        segment.full.length,
        segment.shown + (state.charsPerSec * dtMs) / 1000
      );
      segment.spokenMs += dtMs;
      if (!withinSpeech) segment.blindMs += dtMs;
      upsertCaption(segment.key, 'ai', revealedText(segment.full, segment.shown));
    }

    const retire = (text, opts) => {
      if (text) upsertCaption(segment.key, 'ai', text, opts);
      state.segments.shift();
    };

    const fullyRevealed = segment.shown >= segment.full.length;
    const silenceMs = heardVoice ? now - state.lastVoiceAt : 0;
    const audioEnded = heardVoice ? silenceMs > END_OF_SPEECH_SILENCE_MS : fullyRevealed;

    // A long answer arrives as several segments back to back. Holding the head of the
    // queue until the room goes quiet leaves the rest waiting behind audio that is
    // already playing, which is how the transcript ends up a turn behind and then
    // dumps the backlog in one go. Everything that arrived is on screen, so hand the
    // voice clock over and let the next segment carry on.
    if (segment.closed && fullyRevealed && !audioEnded && state.segments.length > 1) {
      state.carryVoice = true;
      retire(segment.full);
      return;
    }

    if (!audioEnded) return;
    // The stream never closed. Give it a moment in case more text is still on its
    // way, then let the segment go rather than jam every turn behind it.
    if (!segment.closed && now - segment.grewAt < ORPHAN_SEGMENT_MS) return;

    // A barge-in cuts the audio within a moment of the visitor starting to speak, so a
    // visitor turn that opened around the same time as the avatar fell silent is what
    // stopped it. A backchannel earlier in the answer, or a question asked well after
    // the agent finished, is not.
    const bargedIn =
      state.bargeInAt >= segment.activatedAt &&
      state.bargeInAt >= state.lastVoiceAt - BARGE_IN_LEAD_MS &&
      state.bargeInAt <= state.lastVoiceAt + BARGE_IN_LAG_MS;

    if (!fullyRevealed && !bargedIn &&
        silenceMs < END_OF_SPEECH_SILENCE_MS + BARGE_IN_LAG_MS) {
      // The agent stopped with text still held back, and a barge-in is only known here
      // once the visitor's own words reach this document, which trails the moment the
      // avatar went quiet. Wait that out before writing a tail that may never have
      // been spoken.
      return;
    }

    if (!fullyRevealed && bargedIn) {
      // Cut off part way through: the rest of the reply was never spoken, so it is
      // never written. Remember the whole turn, because the backend publishes its
      // committed copy afterwards and that copy still carries the words the avatar
      // never got to. Nothing is learned from a cut turn - see learnSpeakingRate.
      state.cutTurns.push(segment.full);
      if (state.cutTurns.length > CUT_MEMORY) state.cutTurns.shift();
      const spoken = revealedText(segment.full, segment.shown);
      retire(spoken ? withCutMarker(spoken) : '', { cut: true });
      return;
    }

    learnSpeakingRate(state, segment);
    retire(segment.full);
  }, [readAvatarLevel, upsertCaption]);

  const stopReveal = useCallback(() => {
    if (revealTimerRef.current) {
      clearInterval(revealTimerRef.current);
      revealTimerRef.current = null;
    }
    resetRevealState(revealRef.current);
    analyserRef.current = null;
    analyserDataRef.current = null;
  }, []);

  // Auto-scroll transcript to the latest entry
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [captions]);

  const attachVideoTrack = useCallback((track) => {
    if (!track || !videoRef.current || videoAttachedRef.current) return videoAttachedRef.current;
    try {
      track.attach(videoRef.current);
      if (!videoRef.current.srcObject && track.mediaStreamTrack) {
        try {
          videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
        } catch {}
      }
      videoAttachedRef.current = true;
      setTimeout(() => {
        if (videoRef.current?.srcObject) videoRef.current.play().catch(() => {});
      }, 300);
      return true;
    } catch {
      return false;
    }
  }, []);

  const startSession = async () => {
    if (!agent) return;
    try {
      setIsConnecting(true);
      setError(null);

      const timestamp = Date.now();
      const roomName = `test-agent-${agent.id}-${timestamp}`;

      const variablesToSend = {};
      dynamicVariableNames.forEach((name) => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) variablesToSend[name] = value.trim();
      });

      const sessionPayload = {
        room_name: roomName,
        agent_id: agent.id,
        channel: 'test',
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend }),
      };

      const sessionResponse = await api.post('/sessions/test', sessionPayload);
      setSessionId(sessionResponse.data.session_id);

      // identity 'liveavatar-user' is what the backend watches for to activate the avatar
      const tokenResponse = await api.post('/token', {
        room: roomName,
        identity: LOCAL_IDENTITY,
      });
      const { url, token } = tokenResponse.data;

      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: false,
        videoCaptureDefaults: { resolution: { width: 640, height: 360 } },
        defaultAudioCaptureOptions: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const subscribeToAvatarVideo = (participant) => {
        if (!isAvatarParticipant(participant)) return;
        participant.videoTrackPublications.forEach((publication) => {
          if (publication.track) {
            try {
              publication.setVideoQuality(VideoQuality.LOW);
              publication.setVideoFPS(30);
            } catch {}
            setAvatarVideoTrack(publication.track);
            attachVideoTrack(publication.track);
          }
        });
      };

      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
        newRoom.remoteParticipants.forEach(subscribeToAvatarVideo);
      });

      newRoom.on(RoomEvent.Disconnected, () => {
        videoAttachedRef.current = false;
        avatarReadyNotifiedRef.current = false;
        setIsConnected(false);
        setAvatarVideoTrack(null);
      });

      newRoom.on(RoomEvent.ParticipantConnected, subscribeToAvatarVideo);

      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === 'video' && isAvatarParticipant(participant)) {
          try {
            publication.setVideoQuality(VideoQuality.LOW);
            publication.setVideoFPS(30);
          } catch {}
          setAvatarVideoTrack(track);
          if (!attachVideoTrack(track)) {
            setTimeout(() => videoRef.current && track && attachVideoTrack(track), 500);
          }
        } else if (track.kind === 'audio') {
          if (audioRef.current) track.attach(audioRef.current);
          attachAudioAnalyser(track);
        }
      });

      newRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        if (track.kind === 'video' && isAvatarParticipant(participant)) {
          videoAttachedRef.current = false;
          avatarReadyNotifiedRef.current = false;
          setAvatarVideoTrack(null);
          if (videoRef.current) track.detach(videoRef.current);
        }
      });

      // Live transcript, both sides, as it happens
      if (typeof newRoom.registerTextStreamHandler === 'function') {
        try {
          newRoom.registerTextStreamHandler(TRANSCRIPTION_TOPIC, async (reader, participantInfo) => {
            const identity = participantInfo?.identity || '';
            const localIdentity = newRoom.localParticipant?.identity || LOCAL_IDENTITY;
            const speaker = identity === localIdentity ? 'user' : 'ai';
            const segmentId = reader.info?.attributes?.[SEGMENT_ID_ATTR] || reader.info?.id;
            const key = `segment-${speaker}-${segmentId}`;
            let buffer = '';
            // Stamped before a single word is read: the reveal loop needs to know the
            // tail of the turn now playing was never spoken.
            if (speaker === 'user') markBargeIn();
            try {
              for await (const chunk of reader) {
                buffer += chunk;
                // The visitor's own words are already spoken, so show them straight away.
                // The agent's are queued and released in step with the avatar's audio.
                if (speaker === 'user') upsertCaption(key, speaker, buffer);
                else pushAgentText(key, buffer, false);
              }
            } catch {
              // stream ended early; whatever arrived is kept
            }
            if (speaker === 'ai') pushAgentText(key, buffer, true);
          });
        } catch {
          // a handler is already registered on this room
        }
      }

      // Committed turns republished by the backend over the data channel. These arrive
      // late, so they act as a fallback and are deduped against what is already shown.
      newRoom.on(RoomEvent.DataReceived, (payload) => {
        try {
          const data = JSON.parse(new TextDecoder().decode(payload));
          if (data.type === 'transcription' || data.type === 'conversation_item') {
            const speaker = data.speaker === 'user' ? 'user' : 'ai';
            // `interrupted` cannot be read as the truth about this turn. The avatar owns
            // the audio output and never reports playback finished, so the framework
            // holds the speech handle open and the flag ends up set on *every* assistant
            // turn - it fires when the next visitor turn cancels the handle, whether or
            // not the avatar had already finished speaking. That timing is still worth
            // having: it is the earliest notice the panel gets that the visitor has
            // started talking, ahead of their first interim transcript, and it is the
            // only notice at all when a cough interrupts the agent and STT returns
            // nothing. Whether any words were left unspoken is decided where it always
            // was - against the avatar's own audio.
            if (speaker === 'ai' && data.interrupted) markBargeIn();
            addCaption(speaker, data.text, data.images?.length ? chatImagesFromIds(agentId, data.images) : undefined);
          }
        } catch {}
      });

      await newRoom.connect(url, token);
      setRoom(newRoom);

      resetRevealState(revealRef.current);
      if (revealTimerRef.current) clearInterval(revealTimerRef.current);
      revealTimerRef.current = setInterval(tickReveal, REVEAL_TICK_MS);

      await newRoom.localParticipant.setMicrophoneEnabled(true);
      setMicEnabled(true);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Failed to start conversation');
      setIsConnecting(false);
    }
  };

  const stopSession = async () => {
    try {
      if (room) {
        await room.disconnect();
        setRoom(null);
      }
      if (sessionId) await api.post(`/sessions/${sessionId}/end`);
    } catch {
      // ignore stop errors
    } finally {
      stopReveal();
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setSessionId(null);
      setCaptions([]);
      setAvatarVideoTrack(null);
    }
  };

  // Send a typed message into the live session as a user turn
  const sendText = async () => {
    const message = textValue.trim();
    const withImages = imagesEnabled && attachments.readyCount > 0;
    if ((!message && !withImages) || attachments.uploading || !room?.localParticipant || sending) return;
    let images = [];
    try {
      setSending(true);
      // The agent treats an incoming chat message as a user turn: it interrupts
      // whatever is being spoken. Same barge-in as talking over it.
      markBargeIn();
      if (withImages) {
        // Images go on their own topic (app/agent/chat_image_input.py); the echo
        // uses the text the agent records so the two copies dedupe.
        images = attachments.take();
        const ids = images.map((img) => img.id);
        await room.localParticipant.sendText(JSON.stringify({ text: message, images: ids }), { topic: CHAT_IMAGE_TOPIC });
        addCaption('user', imageTranscriptText(message, ids), images);
      } else {
        await room.localParticipant.sendText(message, { topic: CHAT_TOPIC });
        addCaption('user', message); // optimistic echo; backend also publishes the turn
      }
      setTextValue('');
    } catch (err) {
      attachments.restore(images);
      setError('Could not send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleTextKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendText();
    }
  };

  const toggleMic = async () => {
    if (!room?.localParticipant) return;
    const next = !micEnabled;
    try {
      await room.localParticipant.setMicrophoneEnabled(next);
      setMicEnabled(next);
    } catch {}
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (room) room.disconnect();
    };
  }, [room]);

  useEffect(
    () => () => {
      stopReveal();
      audioCtxRef.current?.close().catch(() => {});
      audioCtxRef.current = null;
    },
    [stopReveal]
  );

  const updateVariableValue = (name, value) => {
    if (!name) return;
    setDynamicVariableValues((prev) => ({ ...prev, [name]: value }));
  };

  const getVariableLabel = (varName) =>
    !varName ? '' : varName.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  const closePanel = () => {
    try {
      window.parent?.postMessage({ type: 'braincx-avatar-panel-close' }, '*');
    } catch {}
  };

  if (loading) {
    return (
      <div className="h-screen w-full bg-gray-950 flex items-center justify-center" style={FULL_HEIGHT}>
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-3" />
          <p className="text-gray-400 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="h-screen w-full bg-gray-950 flex items-center justify-center px-6" style={FULL_HEIGHT}>
        <p className="text-red-400 text-center text-sm">{error}</p>
      </div>
    );
  }

  return (
    <div
      className="h-screen w-full flex justify-center bg-gray-950 text-white overflow-hidden"
      style={FULL_HEIGHT}
    >
      {/* Panel column. Capped so the layout stays a panel when the URL is opened
          directly in a full browser window, not just inside the narrow drawer iframe. */}
      <div className="relative flex flex-col h-full w-full max-w-[560px] overflow-hidden" {...imageDrop.dropHandlers}>
        <ChatDropOverlay active={imageDrop.dragActive} accentColor="#9ca3af" isDarkMode />
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-800 bg-gray-900 flex-shrink-0">
          <div className="min-w-0">
            <h1 className="text-sm font-semibold truncate">{agent?.name || 'BrainCX Assistant'}</h1>
            <p className="text-[11px] text-gray-400">
              {isConnected ? 'Connected' : isConnecting ? 'Connecting...' : 'Ready'}
            </p>
          </div>
          <button
            onClick={closePanel}
            className="p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-gray-800 transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Avatar video. The height cap is what keeps the transcript and the composer on
            screen: at 16:9 an uncapped video eats the whole viewport on a short or wide
            window and pushes the call button out of view. */}
        <div className="relative bg-black w-full aspect-video max-h-[45vh] flex items-center justify-center flex-shrink-0">
          {avatarVideoTrack ? (
            <video
              ref={videoRef}
              autoPlay
              playsInline
              className="w-full h-full object-contain"
              onLoadedMetadata={() => videoRef.current?.play().catch(() => {})}
              onCanPlay={() => {
                videoRef.current?.play().catch(() => {});
                if (!avatarReadyNotifiedRef.current && room?.localParticipant) {
                  avatarReadyNotifiedRef.current = true;
                  try {
                    const msg = new TextEncoder().encode(JSON.stringify({ type: 'avatar_video_ready' }));
                    room.localParticipant.publishData(msg, { reliable: true, topic: 'avatar_status' });
                  } catch {}
                }
              }}
            />
          ) : (
            <div className="text-center px-4">
              {isConnected ? (
                <>
                  <Loader2 className="w-10 h-10 animate-spin text-primary mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">Waiting for avatar...</p>
                </>
              ) : (
                <div className="w-16 h-16 mx-auto rounded-full bg-gray-800 flex items-center justify-center">
                  <Phone className="w-8 h-8 text-gray-600" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Dynamic variable inputs (pre-call only) */}
        {!isConnected && dynamicVariableNames.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-800 bg-gray-900/40 flex-shrink-0 max-h-[30vh] overflow-y-auto">
            <div className="grid grid-cols-1 gap-2">
              {dynamicVariableNames.map((varName) => (
                <input
                  key={varName}
                  type="text"
                  value={dynamicVariableValues[varName] || ''}
                  onChange={(e) => updateVariableValue(varName, e.target.value)}
                  placeholder={getVariableLabel(varName)}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                />
              ))}
            </div>
          </div>
        )}

        {/* Transcript */}
        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3 min-h-0">
          {captions.length === 0 ? (
            <p className="text-gray-500 text-sm text-center mt-6">
              {isConnected
                ? 'Speak or type your message below.'
                : 'Start the conversation to begin.'}
            </p>
          ) : (
            captions.map((c) => (
              <div key={c.key} className={`flex ${c.speaker === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm ${
                    c.speaker === 'user'
                      ? 'bg-primary text-white rounded-br-sm'
                      : 'bg-gray-800 text-gray-100 rounded-bl-sm'
                  }`}
                >
                  {c.images?.length ? (
                    <>
                      <ChatMessageImages images={c.images} />
                      {stripImageMarker(c.text)}
                    </>
                  ) : c.text}
                </div>
              </div>
            ))
          )}
          <div ref={transcriptEndRef} />
        </div>

        {error && agent && (
          <div className="px-4 py-2">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        {/* Composer / controls */}
        <div className="border-t border-gray-800 bg-gray-900 p-3 flex-shrink-0">
          {!isConnected ? (
            <button
              onClick={startSession}
              disabled={isConnecting || !agent?.liveavatar_enabled}
              className="w-full px-4 py-3 bg-primary hover:bg-primary/90 text-white rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" /> Connecting...
                </>
              ) : (
                <>
                  <Phone className="w-5 h-5" /> Start Conversation
                </>
              )}
            </button>
          ) : (
            <>
            {imagesEnabled && (
              <ChatAttachmentStrip attachments={attachments} isDarkMode accentColor="#9ca3af" />
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={toggleMic}
                className={`p-2.5 rounded-lg transition-colors flex-shrink-0 ${
                  micEnabled
                    ? 'bg-gray-800 text-white hover:bg-gray-700'
                    : 'bg-red-600/20 text-red-400 hover:bg-red-600/30'
                }`}
                aria-label={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
                title={micEnabled ? 'Mute microphone' : 'Unmute microphone'}
              >
                {micEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
              </button>

              <div className="flex-1 min-w-0 flex items-center gap-2 bg-gray-800 rounded-lg px-3">
                {imagesEnabled && (
                  <>
                    <input
                      ref={imageInputRef}
                      type="file"
                      accept={CHAT_IMAGE_ACCEPT}
                      multiple
                      className="hidden"
                      onChange={(e) => {
                        attachments.addFiles(e.target.files);
                        e.target.value = '';
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => imageInputRef.current?.click()}
                      className="p-1.5 -ml-1.5 rounded-md text-gray-400 hover:bg-gray-700 transition-colors flex-shrink-0"
                      aria-label="Attach image"
                      title="Attach image"
                    >
                      <ImagePlus className="w-4 h-4" />
                    </button>
                  </>
                )}
                <input
                  type="text"
                  value={textValue}
                  onChange={(e) => setTextValue(e.target.value)}
                  onPaste={(e) => {
                    if (!imagesEnabled) return;
                    const files = imageFilesFrom(e.clipboardData);
                    if (files.length) {
                      e.preventDefault();
                      attachments.addFiles(files);
                    }
                  }}
                  onKeyDown={handleTextKeyDown}
                  placeholder="Type a reply..."
                  className="flex-1 bg-transparent py-2.5 text-sm text-white placeholder:text-gray-500 focus:outline-none"
                />
                <button
                  onClick={sendText}
                  disabled={(!textValue.trim() && !(imagesEnabled && attachments.readyCount > 0)) || attachments.uploading || sending}
                  className="p-1.5 rounded-md text-primary hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  aria-label="Send message"
                >
                  <Send className="w-4 h-4" />
                </button>
              </div>

              <button
                onClick={stopSession}
                className="p-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex-shrink-0"
                aria-label="End conversation"
                title="End conversation"
              >
                <PhoneOff className="w-5 h-5" />
              </button>
            </div>
            </>
          )}
        </div>

        <audio ref={audioRef} autoPlay />
      </div>
    </div>
  );
};

const EmbedAvatarPanelPage = ({ agentId }) => <EmbedAvatarPanelPageContent agentId={agentId} />;

export default EmbedAvatarPanelPage;
